const SESSION_RELOAD_KEY = 'cf-chunk-recovery-reloaded'

export function isChunkLoadFailureMessage(message: string): boolean {
  const m = message.toLowerCase()
  return (
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('importing a module script failed') ||
    m.includes('error loading dynamically imported module') ||
    m.includes('failed to load module script') ||
    m.includes('dynamically imported module') ||
    m.includes('mime type') ||
    m.includes('text/html')
  )
}

export function isChunkLoadFailure(reason: unknown): boolean {
  if (reason instanceof Error) {
    if (isChunkLoadFailureMessage(reason.message)) return true
    const cause = (reason as Error & { cause?: unknown }).cause
    if (cause != null && isChunkLoadFailure(cause)) return true
  }
  return isChunkLoadFailureMessage(String(reason ?? ''))
}

function isStaleAssetScriptError(event: ErrorEvent): boolean {
  const target = event.target
  if (!(target instanceof HTMLScriptElement) || !target.src) return false
  try {
    const url = new URL(target.src, window.location.origin)
    return url.origin === window.location.origin && url.pathname.startsWith('/assets/')
  } catch {
    return false
  }
}

/** Reload so the browser picks up fresh index.html + chunk hashes after a deploy. */
export function reloadForStaleChunk(options?: { force?: boolean }): void {
  if (!import.meta.env.PROD || typeof window === 'undefined') return
  if (!options?.force && sessionStorage.getItem(SESSION_RELOAD_KEY)) return
  if (options?.force) {
    sessionStorage.removeItem(SESSION_RELOAD_KEY)
  } else {
    sessionStorage.setItem(SESSION_RELOAD_KEY, '1')
  }
  window.location.reload()
}

function tryAutoReloadForStaleChunk(): void {
  reloadForStaleChunk()
}

/** After a deploy, lazy chunks 404; reload once so the browser picks up fresh index + hashes. */
export function installChunkLoadRecovery(): void {
  if (!import.meta.env.PROD || typeof window === 'undefined') return

  window.addEventListener('unhandledrejection', event => {
    if (!isChunkLoadFailure(event.reason)) return
    if (sessionStorage.getItem(SESSION_RELOAD_KEY)) return
    event.preventDefault()
    tryAutoReloadForStaleChunk()
  })

  window.addEventListener(
    'error',
    event => {
      const fromMessage = Boolean(event.message && isChunkLoadFailureMessage(event.message))
      const fromAssetScript = isStaleAssetScriptError(event)
      if (!fromMessage && !fromAssetScript) return
      if (sessionStorage.getItem(SESSION_RELOAD_KEY)) return
      tryAutoReloadForStaleChunk()
    },
    true,
  )
}
